import React, { useCallback } from "react";

import { MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useAppState } from "../../context/StateContext";
import { Warehouse } from "../../models/warehouse";
import SkeletonComponent from "../Generic/Skeleton";
import { useWarehouseActions } from "./hooks/useActions";
import useStyles, { SelectStyled, Label } from "./styles";
import { getAllWarehouseOption, getEmptyWarehouseOption } from "./util";
import { RolePermissionsResponse } from "Models/resources";

interface Props {
  showAllOption?: boolean;
  warehousesToView?: RolePermissionsResponse[];
}

export function WarehouseSelect(props: Props) {
  const {
    isErrorUpdateUser,
    isFetchingWarehouseList,
    isLoadingUpdateUser,
    isSuccessWarehouseList,
    handleUpdateWarehouse,
    warehouseListMerged,
    setWarehouse,
  } = useWarehouseActions(props?.showAllOption);
  const { warehousesToView } = props;

  const allWarehouseOption = getAllWarehouseOption();
  const emptyWarehouseOption = getEmptyWarehouseOption();

  const { warehouse } = useAppState();

  const { t: translate } = useTranslation();
  const classes = useStyles();

  const checkIfWarehouseExists = (
    warehouses: Warehouse[],
    warehousesToView: RolePermissionsResponse[]
  ) => {
    if (!warehousesToView || warehousesToView?.length === 0) return warehouses;
    return warehouses
      ?.map((warehouse) => {
        const existsInWarehousesToView = warehousesToView?.some(
          (obj) => obj?.objectDetails?.id === warehouse?.id
        );
        if (existsInWarehousesToView) {
          return warehouse;
        }
      })
      ?.filter((warehouse) => !!warehouse);
  };

  const renderOptions = useCallback(() => {
    const option = (item?: Warehouse) => {
      if (!item) return <>{emptyWarehouseOption.name}</>;

      if (item.id === allWarehouseOption.id || item.id === emptyWarehouseOption.id) {
        return <>{item.name}</>;
      }

      return (
        <>
          <Label>{translate("warehouse")}:</Label>
          {item.name}
        </>
      );
    };

    if (warehouseListMerged && warehouseListMerged.length > 0) {
      const warehouses = checkIfWarehouseExists(warehouseListMerged, warehousesToView ?? []);
      if (warehouses?.length === 0) {
        warehouses?.push(emptyWarehouseOption);
      }
      if (!warehouse || !warehouses?.some((wh) => wh?.id === warehouse?.id)) {
        setWarehouse(warehouses[0]);
      }
      return warehouses?.map((item) => {
        return (
          <MenuItem key={item?.id} value={item?.id}>
            {option(item)}
          </MenuItem>
        );
      });
    }
  }, [warehouseListMerged, allWarehouseOption, emptyWarehouseOption, translate, warehousesToView]);

  return (
    <SkeletonComponent
      isLoading={isFetchingWarehouseList || (isErrorUpdateUser && !isSuccessWarehouseList)}
      classesName={classes.skeleton}
    >
      <SelectStyled
        name="warehouseDefault"
        value={warehouse?.id}
        onChange={(event) => handleUpdateWarehouse(Number(event.target.value))}
        disabled={isFetchingWarehouseList}
      >
        {renderOptions()}
      </SelectStyled>
    </SkeletonComponent>
  );
}
